/**
 * Round 80 — A-C-12 Governance & Explainability
 *
 * Single source of truth for every numeric threshold rendered in
 * explanation popovers and the governance/methodology page. These
 * constants MUST stay in lockstep with the server-side rules in:
 *   - get_scope_talent_breakdown / get_scope_talent_summary (R74)
 *   - get_scope_training_breakdown / get_scope_technician_training_usage (R75)
 *   - get_scope_artifact_intelligence (R76)
 *   - get_scope_fit_suggestions (R78)
 *
 * If the server numbers move, these move with them — and only here.
 */

// R74 — Talent analytics
export const DIAGNOSTIC_MIN_SAMPLE_FOR_DISPLAY = 5; // summary card "Insufficient data"
export const DIAGNOSTIC_MIN_SAMPLE_PER_TECH = 3; // per-row diagnostic visibility
export const DIAGNOSTIC_OUTCOME_SCORE = {
  Confirmed: 1,
  Partial: 0.5,
  Misdiagnosis: 0,
} as const;
export const TALENT_BAND_QUARTILE = 0.25; // top / dev = 25%

// R76 — Observed Impact (training effectiveness)
export const BEFORE_AFTER_WINDOW_N = 5;
export const NO_CHANGE_DELTA = 0.02;
export const DIAGNOSTIC_DELTA_MIN_PER_SIDE = 3;
export const OBSERVED_IMPACT_CONFIDENCE = {
  low: { max: 4, label: 'Low (fewer than 5 qualifying technicians)' },
  medium: { min: 5, max: 15, label: 'Medium (5–15 qualifying technicians)' },
  high: { min: 16, label: 'High (more than 15 qualifying technicians)' },
} as const;

// R78 — Fit suggestion signals
export const FIT_SIGNAL_THRESHOLDS = {
  high_efficiency: { min_eff: 1.05, min_jobs: 10 },
  low_comeback_rate: { max_rate: 0.05, min_jobs: 10 },
  strong_diagnostic_accuracy: { min_acc: 0.85, min_outcomes: 5 },
  high_job_volume: { min_jobs: 50 },
  recent_training_activity: { window_days: 90, min_events: 1 },
} as const;
export const FIT_SUGGESTION_GATE = { min_signals: 2, min_jobs: 10 } as const;
export const FIT_CONFIDENCE_RULES = {
  high: 'At least 50 completed jobs and 10 diagnostic outcomes',
  medium: 'At least 20 completed jobs',
  low: 'Below the medium-confidence thresholds',
} as const;

// R75 — Training visibility (counts only, no thresholds)
export const TRAINING_VISIBILITY_NOTE =
  'Counts engagement only — does not measure outcomes or effectiveness.';
